import React, { useState, useRef, useEffect } from "react";
import './DateRangePicker.scss';

/**
 * @typedef {Object} DateRangeValue
 * @property {string} from - The start date of the selected range
 * @property {string} to - The end date of the selected range
 */

/**
 * @typedef {Object} DateRangePickerProps
 * @property {string} name - The name attribute for the date range picker button
 * @property {string} id - The id attribute for the date range picker button
 * @property {DateRangeValue} value - The current value of the date range
 * @property {(value: DateRangeValue) => void} onChange - Callback for date range changes
 */

/**
 * DateRangePicker component that allows users to select a range of dates.
 *
 * @param {DateRangePickerProps} props - The properties for the DateRangePicker component
 * @returns {JSX.Element}
 */
export function DateRangePicker({ name, id, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [localValues, setLocalValues] = useState({
    from: (value && value.from) || "",
    to: (value && value.to) || "",
  });

  const ref = useRef(null);
  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);

  const hasOpenedOnce = useRef(false);
  const hasAutoFocusedTo = useRef(false);

  useEffect(() => {
    setLocalValues({
      from: (value && value.from) || "",
      to: (value && value.to) || "",
    });
  }, [value && value.from, value && value.to]);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    }
    try {
      if (open) {
        document.addEventListener("mousedown", handleClick);
  
        if (!hasOpenedOnce.current && fromInputRef.current) {
          setTimeout(() => {
            fromInputRef.current.focus();
            if (typeof fromInputRef.current.showPicker === "function") {
              fromInputRef.current.showPicker();
            }
          }, 0);
          hasOpenedOnce.current = true;
        }
      }
    } catch (error) {
      console.error("Error adding event listener:", error);
    }
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  useEffect(() => {
    try {
      if (open && localValues.from && toInputRef.current && !hasAutoFocusedTo.current) {
        setTimeout(() => {
          toInputRef.current.focus();
          if (typeof toInputRef.current.showPicker === "function") {
            toInputRef.current.showPicker();
          }
        }, 0);
        hasAutoFocusedTo.current = true;
      }
    } catch (error) {
      console.error("Error focusing to input:", error);
    }
  }, [localValues.from, open]);

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} event - The change event from the input element.
   * @description This function is called when the user selects a new 'from' date.
   * It updates the local state with the new 'from' date value, which will trigger
   * a re-render of the component and update the displayed date range.
   * @returns {void}
   */
  const handleFromChange = (event) => {
    const {value} = event.target;
    setLocalValues((prev) => ({ ...prev, from: value }));
  };

  /** 
   * @param {React.ChangeEvent<HTMLInputElement>} event - The change event from the input element.
   * @description This function is called when the user selects a new 'to' date.
   * It updates the local state with the new 'to' date value, which will trigger
   * a re-render of the component and update the displayed date range.
   * * It also closes the date range picker popover.
   * @returns {void}
  */
  const handleToChange = (event) => {
    const { value } = event.target;
    setLocalValues((prev) => ({ ...prev, to: value }));
    setOpen(false);
  };

  const handleClear = () => {
    setLocalValues({ from: '', to: '' });
    onChange({ from: '', to: '' });
    setOpen(false);
  };

  const isPopulated = () => {
    return localValues.from !== '' || localValues.to !== '';
  };

  useEffect(() => {
    onChange(localValues);
  }, [localValues]);

  return (
    <div ref={ref} className="drp-container">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="drp-button"
        aria-label="Select date range"
        name={name}
        id={id}
      >
        {localValues.from || localValues.to
          ? `${localValues.from} → ${localValues.to}`
          : "Select dates"}
      </button>
      {open && (
        <div className="drp-popover">
          <input
            type="date"
            ref={fromInputRef}
            value={localValues.from}
            onChange={handleFromChange}
            className="drp-input"
            max={localValues.to}
          />
          <span className="drp-arrow">→</span>
          <input
            type="date"
            ref={toInputRef}
            value={localValues.to}
            onChange={handleToChange}
            className="drp-input"
            min={localValues.from}
          />
        </div>
      )}
      {
        isPopulated() &&
        <button type="reset" className="drp-clear" onClick={handleClear}>
          ✕
        </button>
      }
    </div>
  );
}
